/*
 * PLP Scroll Up — toggles back-to-top button visibility past one viewport
 * height of scroll, and smooth-scrolls the window to top on click. Respects
 * prefers-reduced-motion (instant scroll), restores keyboard focus to the
 * main landmark after the scroll completes, and reads --ease-both and
 * --duration-600 tokens from :root with safe constant fallbacks.
 * Hooks: [data-plp-scroll-up], [data-visible], [data-active].
 */
(function () {
  'use strict';

  const button = document.querySelector('[data-plp-scroll-up]');
  if (!button) return;

  const FALLBACK_EASE_BOTH = [0.42, 0, 0.58, 1];
  const FALLBACK_SCROLL_DURATION = 600;

  // Must stay in sync with --ease-both and --duration-600 in src/application.css.
  const rootStyle = getComputedStyle(document.documentElement);
  const easeMatch = rootStyle.getPropertyValue('--ease-both').match(/cubic-bezier\(([^)]+)\)/);
  const easeValues = easeMatch ? easeMatch[1].split(',').map((value) => Number.parseFloat(value)) : [];
  const [x1, y1, x2, y2] = easeValues.length === 4 && easeValues.every(Number.isFinite)
    ? easeValues
    : FALLBACK_EASE_BOTH;
  // Token may be "600ms" or the minified ".6s" — parseFloat drops the unit, so ×1000 for seconds.
  const durationRaw = rootStyle.getPropertyValue('--duration-600').trim();
  const durationNumber = Number.parseFloat(durationRaw);
  const durationValue = durationRaw.endsWith('ms')
    ? durationNumber
    : durationRaw.endsWith('s')
      ? durationNumber * 1000
      : durationNumber;
  const SCROLL_DURATION = Number.isFinite(durationValue) && durationValue > 0
    ? durationValue
    : FALLBACK_SCROLL_DURATION;

  // Newton-Raphson solver for cubic-bezier(x1,y1,x2,y2).
  const easeBoth = (t) => {
    let s = t;
    for (let i = 0; i < 5; i++) {
      const x = 3 * (1 - s) ** 2 * s * x1 + 3 * (1 - s) * s ** 2 * x2 + s ** 3;
      const dx = 3 * (1 - s) ** 2 * x1 + 6 * (1 - s) * s * (x2 - x1) + 3 * s ** 2 * (1 - x2);
      if (Math.abs(dx) < 1e-6) break;
      s -= (x - t) / dx;
    }
    return 3 * (1 - s) ** 2 * s * y1 + 3 * (1 - s) * s ** 2 * y2 + s ** 3;
  };

  // Manual rAF scroll, returns a Promise that resolves when done.
  const animateScrollTop = () => new Promise((resolve) => {
    const startY = window.scrollY;
    if (startY === 0) {
      resolve();
      return;
    }
    const startTime = performance.now();
    const step = (now) => {
      const t = Math.min((now - startTime) / SCROLL_DURATION, 1);
      window.scrollTo(0, startY * (1 - easeBoth(t)));
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        resolve();
      }
    };
    requestAnimationFrame(step);
  });

  // rAF throttle for scroll updates.
  let ticking = false;

  const update = () => {
    const visible = window.scrollY > window.innerHeight;
    button.dataset.visible = visible ? 'true' : 'false';
    if (!visible) button.dataset.active = 'false';
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    },
    { passive: true },
  );

  // Live preference — covers OS toggles mid-session.
  const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  let reduceMotion = motionQuery?.matches ?? false;
  motionQuery?.addEventListener('change', (e) => { reduceMotion = e.matches; });

  button.addEventListener('click', async () => {
    button.dataset.active = 'true';
    if (reduceMotion) {
      window.scrollTo(0, 0);
    } else {
      // Run the rAF animation first; focus lands after to avoid the browser
      // pre-empting the manual scroll with an instant focus-induced scroll.
      await animateScrollTop();
    }
    document.getElementById('MainContent')?.focus({ preventScroll: true });
  });

  update();
})();
